import { Card } from '@mui/material';
import { cloneElement, ReactElement, useMemo } from 'react';
import { RaRecord, useSidebarState } from 'react-admin';

import { DatagridHeaderSort } from '@sh4res/bo-common/components';
import {
  Datagrid,
  CustomDatagridProps as DatagridProps,
} from '@sh4res/bo-common/components/Datagrid';
import { RowClick, useRowClick } from '@sh4res/bo-common/hooks';

export interface DatagridTypedProps<T extends RaRecord = RaRecord>
  extends Omit<DatagridProps, 'children' | 'rowClick'> {
  columnRegistry: Record<keyof T, ReactElement>;
  displayedColumns: (keyof T)[] | readonly (keyof T)[];
  sortableColumns?: (keyof T)[] | readonly (keyof T)[];
  rowClick?: RowClick<T>;
  showCard?: boolean;
  testId?: string;
}

export const DatagridTyped = <T extends RaRecord>({
  columnRegistry,
  displayedColumns,
  sortableColumns,
  rowClick,
  showCard = false,
  testId,
  sx,
  ...rest
}: DatagridTypedProps<T>) => {
  const columns = useMemo(
    () =>
      displayedColumns
        .map((name) =>
          name in columnRegistry
            ? cloneElement(columnRegistry[name], {
                ...columnRegistry[name].props,
                key: columnRegistry[name].key || name,
                source: name,
              })
            : null,
        )
        .filter(Boolean),
    [columnRegistry, displayedColumns],
  );
  const rowClickTransformed = useRowClick(rowClick);
  const [sidebarIsOpen] = useSidebarState();

  const heightSx = showCard
    ? { maxHeight: 'calc(var(--app-height) - 173px)' }
    : { maxHeight: '100%' };

  const content = (
    <Datagrid
      sx={{
        maxWidth: `calc(100vw - ${sidebarIsOpen ? '280px' : '90px'})`,
        ...heightSx,
        overflowX: 'auto',
        overflowY: 'auto',
        scrollbarGutter: 'stable',
        tr: {
          height: '50px',
        },
        ...sx,
      }}
      testId={testId}
      header={
        <DatagridHeaderSort
          sortableColumns={sortableColumns as string[] | undefined}
          testId={testId}
        />
      }
      rowClick={rowClickTransformed}
      {...rest}>
      {columns}
    </Datagrid>
  );

  if (showCard) {
    return <Card sx={{ marginLeft: '15px', marginRight: '15px' }}>{content}</Card>;
  } else {
    return content;
  }
};
